import { analytics } from '@/lib/analytics'
import { logger } from '@/lib/logger'
import { ApiError, NetworkError, post } from '@/services/shared/client'
import { normalizeImage, type StoredImage, type ImageType } from '@/types/image'

export type UploadLifecycleStatus =
  | 'queued'
  | 'initializing'
  | 'uploading'
  | 'finalizing'
  | 'success'
  | 'failed'
  | 'aborted'

export interface UploadProgressState {
  fileId: string
  filename: string
  status: UploadLifecycleStatus
  progress: number
  error?: string
  uploadId?: string
  requestId?: string
}

export interface UploadFailure {
  filename: string
  error: string
}

export interface DirectUploadInitRequest {
  invitationId?: string
  originalFilename: string
  mimeType: string
  maxBytes: number
  expectedImageType: ImageType
}

export interface DirectUploadInitResponse {
  success: boolean
  message?: string
  requestId?: string
  data: {
    uploadId: string
    objectKey: string
    uploadUrl: string
    requiredHeaders?: Record<string, string>
    requestId?: string
  }
}

export interface DirectUploadFinalizeRequest {
  uploadId: string
  objectKey: string
}

export interface DirectUploadFinalizeResponse {
  success: boolean
  message?: string
  requestId?: string
  data: {
    image?: StoredImage
    images?: StoredImage[]
    file?: StoredImage
    requestId?: string
  }
}

export interface DirectUploadAbortRequest {
  uploadId: string
  objectKey: string
}

export interface DirectUploadBatchResult {
  images: StoredImage[]
  failed: UploadFailure[]
}

type UploadAuthState = 'auth' | 'unauth'

interface StartDirectUploadBatchParams {
  files: File[]
  fileIds?: string[]
  invitationId?: string
  authState?: UploadAuthState
  expectedImageType: ImageType
  maxBytes: number
  concurrency: number
  onFileStateChange?: (state: UploadProgressState) => void
}

interface FileRuntimeState {
  file: File
  fileId: string
  startedAt?: number
  uploadId?: string
  objectKey?: string
  requestId?: string
  xhr?: XMLHttpRequest
  abortController?: AbortController
  abortedByUser?: boolean
  // Captured for finalize retry/failure telemetry attribution.
  finalizeAttempts?: number
  lastFinalizeErrorStatus?: number
  lastFinalizeErrorClass?: string
}

export interface DirectUploadBatchTask {
  promise: Promise<DirectUploadBatchResult>
  cancelFile: (fileId: string) => void
  cancelAll: () => void
}

const RETRYABLE_EXPIRED_STATUS = 410
const FINALIZE_RETRY_LIMIT = 1
const FINALIZE_RETRY_BASE_DELAY_MS = 100

// Internal wrapper so worker-level telemetry can attribute failures to init/put/finalize.
class UploadPipelineError extends Error {
  constructor(
    public stage: 'init' | 'put' | 'finalize',
    public cause: unknown
  ) {
    super(stage)
    this.name = 'UploadPipelineError'
  }
}

function getRequestId(payload: {
  requestId?: string
  data?: { requestId?: string }
}): string | undefined {
  return payload.data?.requestId || payload.requestId
}

function getErrorDetails(error: unknown): {
  requestId?: string
  errorId?: string
  statusCode?: number
  errorClass: string
} {
  if (error instanceof ApiError) {
    const data = error.data as
      | { requestId?: string; data?: { requestId?: string }; errorId?: string }
      | undefined
    return {
      requestId: data?.requestId || data?.data?.requestId,
      errorId: error.errorId || data?.errorId,
      statusCode: error.status,
      errorClass: 'api_error',
    }
  }
  if (error instanceof NetworkError) {
    return { errorClass: 'network_error' }
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { errorClass: 'abort_error' }
  }
  return {
    errorClass:
      error instanceof Error ? error.name || 'unknown_error' : 'unknown_error',
  }
}

function normalizeUploadError(error: unknown): string {
  if (error instanceof UploadPipelineError) {
    return normalizeUploadError(error.cause)
  }
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return 'Uploads are temporarily busy. Please wait and retry.'
    }
    if (error.status === 403) {
      return 'You are not authorized to upload this file.'
    }
    if (error.status === 410) {
      return 'Your upload session expired. Please try again.'
    }
    if (error.status === 400) {
      return 'File validation failed. Please check file type and size.'
    }
    return error.message || 'Upload request failed.'
  }

  if (error instanceof NetworkError) {
    return 'Network issue while uploading. Please retry.'
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'Upload was cancelled.'
  }

  return error instanceof Error ? error.message : 'Upload failed.'
}

function pickFinalizedImage(
  response: DirectUploadFinalizeResponse
): StoredImage | undefined {
  return response.data.image ?? response.data.images?.[0] ?? response.data.file
}

function isRetryableFinalizeError(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof ApiError && error.status >= 500)
  )
}

function getFinalizeErrorMeta(error: unknown): {
  statusCode?: number
  errorClass: string
} {
  if (error instanceof ApiError) {
    return { statusCode: error.status, errorClass: 'api_error' }
  }
  if (error instanceof NetworkError) {
    return { errorClass: 'network_error' }
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { errorClass: 'abort_error' }
  }
  return {
    errorClass:
      error instanceof Error ? error.name || 'unknown_error' : 'unknown_error',
  }
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

/** Throttle progress so main thread stays free during upload (keeps scroll responsive). */
function throttleProgress(
  onProgress: (progress: number) => void,
  intervalMs: number,
  minStep: number
): (progress: number) => void {
  let last = 0
  let lastTime = 0
  return (progress: number) => {
    const now = Date.now()
    if (
      progress >= 100 ||
      progress - last >= minStep ||
      now - lastTime >= intervalMs
    ) {
      last = progress
      lastTime = now
      onProgress(progress)
    }
  }
}

function createXhrUploadPromise(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (progress: number) => void,
  runtime: FileRuntimeState
): Promise<void> {
  const throttled = throttleProgress(onProgress, 150, 5)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    runtime.xhr = xhr

    xhr.open('PUT', url, true)
    Object.entries(headers).forEach(([k, v]) => {
      xhr.setRequestHeader(k, v)
    })

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      const progress = Math.round((event.loaded / event.total) * 100)
      throttled(Math.min(Math.max(progress, 0), 100))
    }

    xhr.onload = () => {
      runtime.xhr = undefined
      if (xhr.status >= 200 && xhr.status < 300) {
        throttled(100)
        resolve()
        return
      }

      reject(
        new ApiError(
          xhr.status,
          xhr.status === 410
            ? 'Upload URL expired.'
            : `Upload failed with status ${xhr.status}`
        )
      )
    }

    xhr.onerror = () => {
      runtime.xhr = undefined
      reject(new NetworkError('Network error during file upload.'))
    }

    xhr.onabort = () => {
      runtime.xhr = undefined
      reject(new DOMException('Upload aborted', 'AbortError'))
    }

    xhr.send(file)
  })
}

async function abortUploadBestEffort(
  runtime: FileRuntimeState,
  invitationId?: string,
  options?: { trackAbortedEvent?: boolean }
): Promise<void> {
  if (!runtime.uploadId || !runtime.objectKey) return

  try {
    await post('/library/upload/abort', {
      uploadId: runtime.uploadId,
      objectKey: runtime.objectKey,
    } satisfies DirectUploadAbortRequest)
    // Emit aborted telemetry only for user-driven cancellation, not internal retry cleanup.
    if (options?.trackAbortedEvent !== false) {
      analytics.track('host_upload_file_aborted', {
        upload_id: runtime.uploadId,
        request_id: runtime.requestId,
        invitation_id: invitationId,
        file_count: 1,
        success_count: 0,
        fail_count: 1,
        duration_ms: runtime.startedAt
          ? Date.now() - runtime.startedAt
          : undefined,
      })
    }
  } catch (error) {
    logger.warn('[upload] abort failed (ignored)', {
      filename: runtime.file.name,
      uploadId: runtime.uploadId,
      error: normalizeUploadError(error),
    })
  }
}

async function uploadSingleFile(
  runtime: FileRuntimeState,
  params: {
    invitationId?: string
    expectedImageType: ImageType
    maxBytes: number
    onFileStateChange?: (state: UploadProgressState) => void
  }
): Promise<StoredImage> {
  const { file, fileId } = runtime

  const emit = (
    status: UploadLifecycleStatus,
    progress: number,
    overrides?: Partial<UploadProgressState>
  ) => {
    params.onFileStateChange?.({
      fileId,
      filename: file.name,
      status,
      progress,
      uploadId: runtime.uploadId,
      requestId: runtime.requestId,
      ...overrides,
    })
  }

  let retriedAfterExpiry = false

  while (true) {
    try {
      runtime.startedAt = runtime.startedAt || Date.now()
      emit('initializing', 0)
      runtime.abortController = new AbortController()

      let initResponse: DirectUploadInitResponse
      try {
        initResponse = await post<DirectUploadInitResponse>(
          '/library/upload/init',
          {
            invitationId: params.invitationId,
            originalFilename: file.name,
            mimeType: file.type || 'application/octet-stream',
            maxBytes: params.maxBytes,
            expectedImageType: params.expectedImageType,
          } satisfies DirectUploadInitRequest
        )
      } catch (error) {
        throw new UploadPipelineError('init', error)
      }

      runtime.uploadId = initResponse.data.uploadId
      runtime.objectKey = initResponse.data.objectKey
      runtime.requestId = getRequestId(initResponse)

      const uploadUrl = initResponse.data.uploadUrl
      try {
        const parsed = new URL(uploadUrl)
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          throw new Error(`Invalid upload URL scheme: ${parsed.protocol}`)
        }
      } catch (err) {
        if (err instanceof TypeError) {
          throw new UploadPipelineError('init', new Error('Invalid upload URL'))
        }
        throw new UploadPipelineError(
          'init',
          err instanceof Error ? err : new Error(String(err))
        )
      }

      emit('uploading', 1)

      if (runtime.abortedByUser) {
        throw new DOMException('Upload aborted', 'AbortError')
      }

      try {
        await createXhrUploadPromise(
          uploadUrl,
          file,
          initResponse.data.requiredHeaders ?? {},
          (progress) => emit('uploading', progress),
          runtime
        )
      } catch (error) {
        throw new UploadPipelineError('put', error)
      }

      if (runtime.abortedByUser) {
        throw new DOMException('Upload aborted', 'AbortError')
      }

      emit('finalizing', 100)
      const finalizePayload = {
        uploadId: runtime.uploadId!,
        objectKey: runtime.objectKey!,
      } satisfies DirectUploadFinalizeRequest
      let finalizeResponse: DirectUploadFinalizeResponse | undefined
      let finalizeRetried = false
      // Reset per-file finalize diagnostics before each finalize sequence.
      runtime.finalizeAttempts = 0
      runtime.lastFinalizeErrorStatus = undefined
      runtime.lastFinalizeErrorClass = undefined
      for (let attempt = 0; attempt <= FINALIZE_RETRY_LIMIT; attempt += 1) {
        try {
          runtime.finalizeAttempts = attempt + 1
          // Finalize is idempotent on uploadId+objectKey, so retries keep identical payload.
          finalizeResponse = await post<DirectUploadFinalizeResponse>(
            '/library/upload/finalize',
            finalizePayload
          )
          break
        } catch (error) {
          const { statusCode, errorClass } = getFinalizeErrorMeta(error)
          runtime.lastFinalizeErrorStatus = statusCode
          runtime.lastFinalizeErrorClass = errorClass
          if (
            attempt < FINALIZE_RETRY_LIMIT &&
            isRetryableFinalizeError(error) &&
            !runtime.abortedByUser
          ) {
            finalizeRetried = true
            const backoffMs = FINALIZE_RETRY_BASE_DELAY_MS * 2 ** attempt
            logger.warn('[upload] finalize transient failure, retrying once', {
              filename: runtime.file.name,
              uploadId: runtime.uploadId,
              objectKey: runtime.objectKey,
              attempt: attempt + 1,
              nextAttempt: attempt + 2,
              statusCode,
              errorClass,
              backoffMs,
              error: normalizeUploadError(error),
            })
            await sleep(backoffMs)
            continue
          }
          throw new UploadPipelineError('finalize', error)
        }
      }

      if (!finalizeResponse) {
        throw new Error('Finalize did not return a response.')
      }

      const image = pickFinalizedImage(finalizeResponse)
      if (!image) {
        throw new Error('Finalize did not return an image.')
      }

      if (finalizeRetried) {
        analytics.track('host_upload_file_finalize_retry_succeeded', {
          invitation_id: params.invitationId,
          upload_id: runtime.uploadId,
          request_id: getRequestId(finalizeResponse) || runtime.requestId,
          finalize_attempt: runtime.finalizeAttempts,
          previous_status_code: runtime.lastFinalizeErrorStatus,
          previous_error_class: runtime.lastFinalizeErrorClass,
          filename: runtime.file.name,
          file_count: 1,
          success_count: 1,
          fail_count: 0,
          duration_ms: runtime.startedAt
            ? Date.now() - runtime.startedAt
            : undefined,
        })
      }

      emit('success', 100, {
        requestId: getRequestId(finalizeResponse) || runtime.requestId,
      })
      return normalizeImage(image)
    } catch (error) {
      const sourceError =
        error instanceof UploadPipelineError ? error.cause : error
      const isExpired =
        sourceError instanceof ApiError &&
        sourceError.status === RETRYABLE_EXPIRED_STATUS

      // Expired upload URLs/sessions are recoverable: abort stale upload state and re-init once.
      if (!runtime.abortedByUser && isExpired && !retriedAfterExpiry) {
        retriedAfterExpiry = true
        await abortUploadBestEffort(runtime, params.invitationId, {
          trackAbortedEvent: false,
        })
        // Clear stale identifiers so the recovery path must use a fresh init payload.
        runtime.uploadId = undefined
        runtime.objectKey = undefined
        runtime.requestId = undefined
        continue
      }

      if (runtime.abortedByUser) {
        emit('aborted', 0)
        throw new DOMException('Upload aborted', 'AbortError')
      }

      emit('failed', 0, { error: normalizeUploadError(error) })
      throw error
    } finally {
      runtime.abortController = undefined
    }
  }
}

export function startDirectUploadBatch(
  params: StartDirectUploadBatchParams
): DirectUploadBatchTask {
  const safeConcurrency = Math.max(1, params.concurrency)
  const runtimeById = new Map<string, FileRuntimeState>()
  const images: StoredImage[] = []
  const failed: UploadFailure[] = []
  const startedAt = Date.now()

  params.files.forEach((file, index) => {
    const fileId =
      params.fileIds?.[index] ||
      `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`
    runtimeById.set(fileId, { file, fileId })
    params.onFileStateChange?.({
      fileId,
      filename: file.name,
      status: 'queued',
      progress: 0,
    })
  })

  const queue = Array.from(runtimeById.values())

  const worker = async () => {
    while (queue.length > 0) {
      const runtime = queue.shift()
      if (!runtime) return
      if (runtime.abortedByUser) continue

      try {
        const image = await uploadSingleFile(runtime, {
          invitationId: params.invitationId,
          expectedImageType: params.expectedImageType,
          maxBytes: params.maxBytes,
          onFileStateChange: params.onFileStateChange,
        })
        images.push(image)
      } catch (error) {
        if (runtime.abortedByUser) {
          continue
        }
        const normalized = normalizeUploadError(error)
        failed.push({ filename: runtime.file.name, error: normalized })

        const stage =
          error instanceof UploadPipelineError ? error.stage : undefined
        const sourceError =
          error instanceof UploadPipelineError ? error.cause : error
        const isInitFailure =
          stage === 'init' ||
          (!runtime.uploadId &&
            !(
              sourceError instanceof DOMException &&
              sourceError.name === 'AbortError'
            ))
        if (isInitFailure) {
          const initError = getErrorDetails(sourceError)
          analytics.track('host_upload_file_init_failed', {
            invitation_id: params.invitationId,
            auth_state: params.authState,
            request_id: initError.requestId,
            error_id: initError.errorId,
            status_code: initError.statusCode,
            error_class: initError.errorClass,
            filename: runtime.file.name,
            file_count: 1,
            success_count: 0,
            fail_count: 1,
            duration_ms: runtime.startedAt
              ? Date.now() - runtime.startedAt
              : undefined,
          })
          continue
        }

        if (stage === 'finalize') {
          analytics.track('host_upload_file_finalize_failed', {
            invitation_id: params.invitationId,
            upload_id: runtime.uploadId,
            request_id: runtime.requestId,
            finalize_attempt: runtime.finalizeAttempts,
            status_code: runtime.lastFinalizeErrorStatus,
            error_class: runtime.lastFinalizeErrorClass,
            filename: runtime.file.name,
            file_count: 1,
            success_count: 0,
            fail_count: 1,
            duration_ms: runtime.startedAt
              ? Date.now() - runtime.startedAt
              : undefined,
          })
        } else if (stage === 'put') {
          analytics.track('host_upload_file_put_failed', {
            invitation_id: params.invitationId,
            upload_id: runtime.uploadId,
            request_id: runtime.requestId,
            filename: runtime.file.name,
            file_count: 1,
            success_count: 0,
            fail_count: 1,
            duration_ms: runtime.startedAt
              ? Date.now() - runtime.startedAt
              : undefined,
          })
        } else if (
          sourceError instanceof ApiError &&
          sourceError.status >= 400
        ) {
          analytics.track('host_upload_file_finalize_failed', {
            invitation_id: params.invitationId,
            upload_id: runtime.uploadId,
            request_id: runtime.requestId,
            finalize_attempt: runtime.finalizeAttempts,
            status_code: runtime.lastFinalizeErrorStatus || sourceError.status,
            error_class: runtime.lastFinalizeErrorClass || 'api_error',
            filename: runtime.file.name,
            file_count: 1,
            success_count: 0,
            fail_count: 1,
            duration_ms: runtime.startedAt
              ? Date.now() - runtime.startedAt
              : undefined,
          })
        }
      }
    }
  }

  const promise = (async () => {
    analytics.track('host_upload_batch_started', {
      invitation_id: params.invitationId,
      file_count: params.files.length,
    })

    await Promise.all(
      Array.from({ length: Math.min(safeConcurrency, queue.length || 1) }, () =>
        worker()
      )
    )

    analytics.track('host_upload_batch_completed', {
      invitation_id: params.invitationId,
      file_count: params.files.length,
      success_count: images.length,
      fail_count: failed.length,
      duration_ms: Date.now() - startedAt,
    })

    return { images, failed }
  })()

  const cancelFile = (fileId: string) => {
    const runtime = runtimeById.get(fileId)
    if (!runtime) return
    if (runtime.abortedByUser) return

    runtime.abortedByUser = true
    runtime.xhr?.abort()
    runtime.abortController?.abort()
    // Cancellation should never block UI; server abort is best-effort only.
    void abortUploadBestEffort(runtime, params.invitationId)
    params.onFileStateChange?.({
      fileId: runtime.fileId,
      filename: runtime.file.name,
      status: 'aborted',
      progress: 0,
      uploadId: runtime.uploadId,
      requestId: runtime.requestId,
    })
  }

  const cancelAll = () => {
    for (const runtime of runtimeById.values()) {
      cancelFile(runtime.fileId)
    }
  }

  return { promise, cancelFile, cancelAll }
}
